import { createColumnHelper, type SortingFn } from '@tanstack/react-table';

import {
  ATTENDANCE_REPORT_COLUMN,
  type AttendanceReportRow,
} from '~entities/student/api/reporting-attendance-report.types';

import { DataTableColumnHeader } from '~shared/components/table';

import { compareReportCalendarDates } from './attendance-report.helpers';

const columnHelper = createColumnHelper<AttendanceReportRow>();

const calendarDateSortingFn: SortingFn<AttendanceReportRow> = (
  rowA,
  rowB,
  columnId,
) => {
  return compareReportCalendarDates(
    String(rowA.getValue(columnId) ?? ''),
    String(rowB.getValue(columnId) ?? ''),
  );
};

export const attendanceReportColumns = [
  columnHelper.accessor('studentName', {
    header: (info) => (
      <DataTableColumnHeader
        disableHiding
        column={info.column}
        title={ATTENDANCE_REPORT_COLUMN.STUDENT_NAME}
      />
    ),
    cell: (info) => info.getValue() || '—',
  }),
  columnHelper.accessor('studentId', {
    header: (info) => (
      <DataTableColumnHeader
        disableHiding
        column={info.column}
        title={ATTENDANCE_REPORT_COLUMN.STUDENT_ID}
      />
    ),
    cell: (info) => (
      <span className='break-all font-mono text-xs'>{info.getValue()}</span>
    ),
  }),
  columnHelper.accessor('email', {
    header: (info) => (
      <DataTableColumnHeader
        disableHiding
        column={info.column}
        title={ATTENDANCE_REPORT_COLUMN.EMAIL}
      />
    ),
    cell: (info) => info.getValue() || '—',
  }),
  columnHelper.accessor('phone', {
    header: (info) => (
      <DataTableColumnHeader
        disableHiding
        column={info.column}
        title={ATTENDANCE_REPORT_COLUMN.PHONE}
      />
    ),
    cell: (info) => info.getValue() || '—',
  }),
  columnHelper.accessor('studentSinceDate', {
    header: (info) => (
      <DataTableColumnHeader
        disableHiding
        column={info.column}
        title={ATTENDANCE_REPORT_COLUMN.STUDENT_SINCE_DATE}
      />
    ),
    sortingFn: calendarDateSortingFn,
    cell: (info) => info.getValue() || '—',
  }),
  columnHelper.accessor('lifetimeAttendance', {
    header: (info) => (
      <DataTableColumnHeader
        disableHiding
        column={info.column}
        title={ATTENDANCE_REPORT_COLUMN.TOTAL_LIFETIME_ATTENDANCE}
      />
    ),
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor('attendanceLast30Days', {
    header: (info) => (
      <DataTableColumnHeader
        disableHiding
        column={info.column}
        title={ATTENDANCE_REPORT_COLUMN.ATTENDANCE_LAST_30_DAYS}
      />
    ),
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor('totalAbsences', {
    header: (info) => (
      <DataTableColumnHeader
        disableHiding
        column={info.column}
        title={ATTENDANCE_REPORT_COLUMN.TOTAL_ABSENCES}
      />
    ),
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor('absencesInRange', {
    header: (info) => (
      <DataTableColumnHeader
        disableHiding
        column={info.column}
        title={ATTENDANCE_REPORT_COLUMN.ABSENCES_IN_SELECTED_DATE_RANGE}
      />
    ),
    cell: (info) => info.getValue() ?? '—',
  }),
  columnHelper.accessor('lastAttendedClass', {
    header: (info) => (
      <DataTableColumnHeader
        disableHiding
        column={info.column}
        title={ATTENDANCE_REPORT_COLUMN.LAST_ATTENDED_CLASS}
      />
    ),
    cell: (info) => info.getValue() || '—',
  }),
  columnHelper.accessor('lastAttendanceDate', {
    header: (info) => (
      <DataTableColumnHeader
        disableHiding
        column={info.column}
        title={ATTENDANCE_REPORT_COLUMN.LAST_ATTENDANCE_DATE}
      />
    ),
    sortingFn: calendarDateSortingFn,
    cell: (info) => info.getValue() || '—',
  }),
  columnHelper.accessor('lastAttendanceTime', {
    header: (info) => (
      <DataTableColumnHeader
        disableHiding
        column={info.column}
        title={ATTENDANCE_REPORT_COLUMN.LAST_ATTENDANCE_TIME}
      />
    ),
    cell: (info) => info.getValue() || '—',
  }),
  columnHelper.accessor('upcomingScheduledClass', {
    header: (info) => (
      <DataTableColumnHeader
        disableHiding
        column={info.column}
        title={ATTENDANCE_REPORT_COLUMN.UPCOMING_SCHEDULED_CLASS}
      />
    ),
    cell: (info) => info.getValue() || '—',
  }),
  columnHelper.accessor('upcomingScheduledDate', {
    header: (info) => (
      <DataTableColumnHeader
        disableHiding
        column={info.column}
        title={ATTENDANCE_REPORT_COLUMN.UPCOMING_SCHEDULED_DATE}
      />
    ),
    sortingFn: calendarDateSortingFn,
    cell: (info) => info.getValue() || '—',
  }),
  columnHelper.accessor('upcomingScheduledTime', {
    header: (info) => (
      <DataTableColumnHeader
        disableHiding
        column={info.column}
        title={ATTENDANCE_REPORT_COLUMN.UPCOMING_SCHEDULED_TIME}
      />
    ),
    cell: (info) => info.getValue() || '—',
  }),
];
